import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountWorkflowStatus, SubPartnerAccountStatus } from 'vopay';

/**
 * Raw API value stays the logic key (badge colour via `GetColorStatus`, `Blocked`/`Active` branches);
 * only the rendered string is localized (i18n-conventions rule). Falls back to the raw status string
 * (not the i18next key path) when a status isn't mapped, so an unrecognized value still renders
 * something readable instead of `Global.PartnerAccountStatus.Foo`.
 *
 * `AccountWorkflowStatus` is every status the account workflow can leave an account in: the
 * `PartnerAccountStatus` set an `AccountBalance.AccountStatus` carries, plus the extended statuses the
 * Onboarding Tracker shows (e.g. `Underwriting Review`). `SubPartnerAccountStatus` is the partner
 * account's own workflow, which overlaps but is not a subset. The `Record` below is exhaustive over
 * both unions, so tsc fails if either gains a member without a label; a status added to the backend
 * before the unions catch up still renders (via the raw-string fallback), just untranslated until it's
 * added here.
 *
 * Memoized on `t`, whose identity changes only when the language does.
 */
export default function usePartnerAccountStatusLabel() {
  const { t } = useTranslation('translations', { keyPrefix: 'Global.PartnerAccountStatus' });

  return useMemo(() => {
    const labelsByStatus: Record<AccountWorkflowStatus | SubPartnerAccountStatus, string> = {
      'Pre-Application Check': t('Pre-Application Check', { defaultValue: 'Pre-Application Check' }),
      'Account Review and Validation': t('Account Review and Validation', {
        defaultValue: 'Account Review and Validation',
      }),
      'Sales Manager Approval': t('Sales Manager Approval', { defaultValue: 'Sales Manager Approval' }),
      'Agreement Pending': t('Agreement Pending', { defaultValue: 'Agreement Pending' }),
      'Application Pending': t('Application Pending', { defaultValue: 'Application Pending' }),
      'Setup Fee Pending': t('Setup Fee Pending', { defaultValue: 'Setup Fee Pending' }),
      Underwriting: t('Underwriting', { defaultValue: 'Underwriting' }),
      'Credit Risk Review': t('Credit Risk Review', { defaultValue: 'Credit Risk Review' }),
      'Account Provisioning': t('Account Provisioning', { defaultValue: 'Account Provisioning' }),
      'Certification Pending': t('Certification Pending', { defaultValue: 'Certification Pending' }),
      Active: t('Active', { defaultValue: 'Active' }),
      Disabled: t('Disabled', { defaultValue: 'Disabled' }),
      Rejected: t('Rejected', { defaultValue: 'Rejected' }),
      'Implementation Fee Pending': t('Implementation Fee Pending', {
        defaultValue: 'Implementation Fee Pending',
      }),
      'Document Review': t('Document Review', { defaultValue: 'Document Review' }),
      'Manager Approval': t('Manager Approval', { defaultValue: 'Manager Approval' }),
      Blocked: t('Blocked', { defaultValue: 'Blocked' }),
      'Risk Review': t('Risk Review', { defaultValue: 'Risk Review' }),
      'Compliance Review': t('Compliance Review', { defaultValue: 'Compliance Review' }),
      'Finance Review': t('Finance Review', { defaultValue: 'Finance Review' }),
      'Pending Sandbox Approval': t('Pending Sandbox Approval', { defaultValue: 'Pending Sandbox Approval' }),
      'Finance Approval': t('Finance Approval', { defaultValue: 'Finance Approval' }),
      Suspended: t('Suspended', { defaultValue: 'Suspended' }),
      'Underwriting Review': t('Underwriting Review', { defaultValue: 'Underwriting Review' }),
      'Security Deposit Pending': t('Security Deposit Pending', { defaultValue: 'Security Deposit Pending' }),
      'Third Party Review': t('Third Party Review', { defaultValue: 'Third Party Review' }),
    };

    const labels: Record<string, string> = labelsByStatus;

    return {
      /**
       * Takes `string`, not a union, on purpose: the backend keys its statuses off the live enum and
       * can send one the unions have not caught up with. Falls back to the raw status string then.
       */
      resolveStatusLabel: (rawStatus: string): string => labels[rawStatus] ?? rawStatus,
    };
  }, [t]);
}
